import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, Brain, Target, Eye, Heart, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const EducationalSlides = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "This is where your transformation begins ✨",
      subtitle: "",
      content: "In just a few minutes, you'll uncover what's been holding you back and discover your personal roadmap to growth.",
      icon: <Sparkles className="w-20 h-20 text-rose-500" />,
      bgGradient: "from-rose-100 to-pink-100"
    },
    {
      title: "Neuroplasticity: Your brain is flexible",
      subtitle: "Your Brain Can Change",
      content: "Your brain can form new pathways. That means the habits and beliefs holding you back can be rewired into self-love and confidence.",
      icon: <Brain className="w-20 h-20 text-purple-500" />,
      bgGradient: "from-purple-100 to-pink-100"
    },
    {
      title: "Our proven 4-step framework",
      subtitle: "The NeuroLove Method",
      content: "Reflect 🔍 → Reframe ✨ → Rewire 🌱 → Reinforce 🌸. These steps guide everything inside Constant Love to help you shift your patterns and step into your best self.",
      icon: <Target className="w-20 h-20 text-blue-500" />,
      bgGradient: "from-blue-100 to-cyan-100"
    },
    {
      title: "Metacognition: The key to awareness",
      subtitle: "Think About Your Thinking",
      content: "Metacognition means noticing your thoughts instead of being ruled by them. When you see your patterns clearly, you can finally change them.",
      icon: <Eye className="w-20 h-20 text-indigo-500" />,
      bgGradient: "from-indigo-100 to-purple-100"
    },
    {
      title: "Clarity before growth",
      subtitle: "Why This Matters",
      content: "You can't change what you don't see. This assessment will show where you are now, what's been holding you back, and the path forward.",
      icon: <Sparkles className="w-20 h-20 text-yellow-500" />,
      bgGradient: "from-yellow-100 to-orange-100"
    },
    {
      title: "Your AI companion & guide",
      subtitle: "Meet Conchi 💖",
      content: "Conchi will use your answers to support you every day. She'll reflect your patterns, encourage you, and guide you with the NeuroLove Method.",
      icon: <Heart className="w-20 h-20 text-rose-500" />,
      bgGradient: "from-rose-100 to-red-100"
    },
    {
      title: "Your data is private & secure",
      subtitle: "100% Confidential",
      content: "Everything you share stays between you and your AI companion. We never share your personal information with anyone.",
      icon: <Lock className="w-20 h-20 text-green-500" />,
      bgGradient: "from-green-100 to-emerald-100"
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Slide {currentSlide + 1} of {slides.length}</span>
            <span className="text-sm font-semibold text-rose-600">{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-rose-400 to-pink-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide content */}
        <div className={`text-center mb-8 bg-gradient-to-br ${slides[currentSlide].bgGradient} rounded-xl p-8`}>
          <div className="flex justify-center mb-6">
            {slides[currentSlide].icon}
          </div>
          
          {slides[currentSlide].subtitle && (
            <p className="text-sm uppercase tracking-wider text-gray-600 mb-2 font-semibold">
              {slides[currentSlide].subtitle}
            </p>
          )}
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {slides[currentSlide].title}
          </h2>
          
          <p className="text-lg text-gray-700 leading-relaxed">
            {slides[currentSlide].content}
          </p>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            disabled={currentSlide === 0}
            variant="outline"
            className="flex items-center disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            className="bg-rose-500 hover:bg-rose-600 text-white flex items-center"
          >
            {currentSlide === slides.length - 1 ? "Start Assessment" : "Next"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EducationalSlides;